import { playSample } from './audio'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import { pickRandom } from './primitives/pickRandom'
import {
  TemplateCloneRestorer,
  createTemplateCloneRestorer,
} from './primitives/restoreClonedTemplates'
import { spawnProgrammedClone } from './sharedHelpers'
import {
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
  BehaviorContext,
} from './types'

/**
 * RJS10 のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS10.rbxl）のギミックを移植する:
 * - ServerScriptService/BlockScript: プレイ開始時に、ServerStorage の
 *   ①あおいブロック / ②あかいブロック / ③みどりのブロック から
 *   ランダムに選んだクローンを 5 行 × 4 列 = 20 個配置する
 *   （Lua: rand:NextInteger(1,3) → Position = (j*25-62.5, 10, i*-10-20)）。
 * - ServerScriptService/EventScript + StarterGui/GUILocalScript:
 *   BindableEvent「ゲームオーバー」で GameOverSound + GameoverTextLabel、
 *   「クリア」で ClearSound + ClearTextLabel を表示する。プレビューでは
 *   学習者コードの「メッセージを送る」が同名メッセージとして届く。
 *
 * プレビューでの近似・意図的な差異:
 * - 実機の 2 つの TextLabel は同位置（最上部の帯）に重なって表示されるため、
 *   単一スロットの HUD テキスト（後着優先）でも見た目は実機と同等になる。
 * - ServerStorage のテンプレート（ブロック 3 種・ボール 3 種・⑨かべ）は
 *   シーンに代役として置かれているので、実機同様プレイ中は隠す。
 *
 * 未対応: なし（Tool・装備系のギミックはこのミッションに存在しない）。
 */

/** ブロックを並べる行数（Lua: for i = 1, 5）。 */
const BLOCK_ROWS = 5

/** ブロックを並べる列数（Lua: for j = 1, 4）。 */
const BLOCK_COLUMNS = 4

/** クローン元になるブロックテンプレートのオブジェクト名（rand 1..3 の対応）。 */
const BLOCK_TEMPLATE_NAMES: ReadonlyArray<string> = [
  '①あおいブロック',
  '②あかいブロック',
  '③みどりのブロック',
]

/**
 * 実機では ServerStorage に置かれている全テンプレートのオブジェクト名。
 * ブロック 3 種は BlockScript が、ボール 3 種と ⑨かべ は学習者プログラムの
 * クローンブロックが複製元として使う。
 */
const SERVER_STORAGE_TEMPLATE_NAMES: ReadonlyArray<string> = [
  ...BLOCK_TEMPLATE_NAMES,
  '⑤あおいボール',
  '⑥あかいボール',
  '⑦みどりのボール',
  '⑨かべ',
]

/** ゲームオーバー GUI の実機デザイン（GameoverTextLabel の実測値）。 */
const GAMEOVER_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#005500',
  borderWidth: 4,
  wide: true,
}

/** クリア GUI の実機デザイン（ClearTextLabel の実測値）。 */
const CLEAR_HUD_STYLE: HudStyle = {
  background: '#FF5500',
  color: '#FFFFFF',
  border: '#AA0000',
  borderWidth: 8,
  wide: true,
}

/** GUI の文言（実機 TextLabel の Text）。 */
const GAMEOVER_TEXT = 'ゲームオーバー・・・'
const CLEAR_TEXT = 'クリア！！！'

/**
 * ブロック 1 個の配置座標（Lua: Vector3.new(j * 25 - 50 - 12.5, 10, i * -10 - 20)）。
 */
const blockPosition = (
  row: number,
  column: number,
): { x: number; y: number; z: number } => ({
  x: column * 25 - 62.5,
  y: 10,
  z: row * -10 - 20,
})

/**
 * BlockScript の移植。プレイ開始時にランダムな色のブロックを 20 個配置する。
 * Roblox の Clone() は配下の Script ごと複製するため、クローンには学習者が
 * テンプレートに付けたプログラムを起動する。
 */
const runBlockScript = (
  ctx: BehaviorContext,
  templateRestorer: TemplateCloneRestorer,
): void => {
  const { world } = ctx
  // 実機の ServerStorage 同様、テンプレート本体はプレイ中隠す。クローンの
  // 複製元は隠蔽前のスナップショット（実機の ServerStorage に保存された状態に
  // 相当。学習者の control_clone 複製への状態復元も同じスナップショットで
  // 行う。詳細は primitives/restoreClonedTemplates.ts）。
  const snapshotByName = templateRestorer.concealAndSnapshot(world)
  const blockSnapshots = BLOCK_TEMPLATE_NAMES.flatMap((name) => {
    const snapshot = snapshotByName.get(name)
    return snapshot ? [snapshot] : []
  })
  // テンプレートが欠けたシーン（ハードコードシーン等）では配置しない。
  if (blockSnapshots.length !== BLOCK_TEMPLATE_NAMES.length) {
    return
  }
  for (let row = 1; row <= BLOCK_ROWS; row += 1) {
    for (let column = 1; column <= BLOCK_COLUMNS; column += 1) {
      // Lua: rand:NextInteger(1,3) で 3 色から等確率に選ぶ。
      const template = pickRandom(blockSnapshots)
      spawnProgrammedClone(
        ctx,
        template,
        'rjs10_block',
        blockPosition(row, column),
      )
    }
  }
}

export const createRjs10Behaviors = (): MissionBehaviors => {
  // テンプレートの隠蔽 + 学習者の control_clone 複製への状態復元。
  const templateRestorer = createTemplateCloneRestorer(
    SERVER_STORAGE_TEMPLATE_NAMES,
  )

  const onMessage = new Map<string, BehaviorHandler>()

  // Lua: storage["ゲームオーバー"].Event → GameOverSound:Play() +
  // GameoverTextLabel.Visible = true（表示は出しっぱなし）。
  onMessage.set('ゲームオーバー', (ctx) => {
    playSample(FAIL_GAMEOVER_SFX.url, FAIL_GAMEOVER_SFX.volume, ctx.signal)
    ctx.setHudText?.(GAMEOVER_TEXT, GAMEOVER_HUD_STYLE)
  })

  // Lua: storage["クリア"].Event → ClearSound:Play() +
  // ClearTextLabel.Visible = true（表示は出しっぱなし）。
  onMessage.set('クリア', (ctx) => {
    playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
    ctx.setHudText?.(CLEAR_TEXT, CLEAR_HUD_STYLE)
  })

  return {
    // watchClones は runBlockScript（隠蔽）と closure 経由でスナップショットを
    // 共有するため末尾でよい（既存テストの onPlay[0] 参照も維持される）。
    onPlay: [
      (ctx) => runBlockScript(ctx, templateRestorer),
      templateRestorer.watchClones,
    ],
    onMessage,
    // ServerStorage のテンプレート本体では学習者プログラムを起動しない
    // （クローンには startObjectPrograms / control_clone 経由で起動する）。
    serverStorageTemplates: SERVER_STORAGE_TEMPLATE_NAMES,
  }
}
